/**
 * Reads the GA4 client id out of the `_ga` cookie.
 *
 * `sign_up_completed` is emitted server-side (see the backend's user lifecycle)
 * so that every registration path is counted, not just this one. The trade-off
 * is that the server has no idea which browser the signup came from, and a
 * Measurement Protocol event without a `client_id` is attributed to a brand-new
 * GA4 user — the signup is counted but loses its channel/campaign.
 *
 * Sending the cookie's client id along with the registration payload closes
 * that gap for email/password signups. OAuth signups cannot do this: Strapi
 * creates the account during the provider callback, before any of our code
 * runs, so those fall back to a synthetic id on the server.
 *
 * Cookie format is `GA1.1.<clientId>` where clientId is `<random>.<timestamp>`,
 * e.g. `GA1.1.1234567890.1699999999` -> `1234567890.1699999999`.
 */
export function getGaClientId(cookieString?: string): string | undefined {
  const source = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '');
  if (!source) return undefined;

  const match = source
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('_ga='));
  if (!match) return undefined;

  const value = decodeURIComponent(match.slice('_ga='.length));
  // Drop the `GA1.1.` version/depth prefix; the remainder is the client id.
  const segments = value.split('.');
  if (segments.length < 4) return undefined;

  const clientId = segments.slice(2).join('.');
  return clientId || undefined;
}
